<template>
  <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5">
    <!-- Navegação do mês -->
    <div class="flex items-center justify-between mb-4">
      <button @click="prevMonth" class="p-2 rounded-lg hover:bg-gray-100 transition">
        <span class="mdi mdi-chevron-left text-xl text-[#152a34]"></span>
      </button>
      <h3 class="font-semibold text-[#152a34] capitalize">
        {{ monthLabel }}
      </h3>
      <button @click="nextMonth" class="p-2 rounded-lg hover:bg-gray-100 transition">
        <span class="mdi mdi-chevron-right text-xl text-[#152a34]"></span>
      </button>
    </div>

    <!-- Dias da semana -->
    <div class="grid grid-cols-7 mb-2">
      <div
        v-for="day in weekDays"
        :key="day"
        class="text-center text-xs font-medium text-gray-400 py-1"
      >{{ day }}</div>
    </div>

    <!-- Loading do calendário -->
    <div v-if="loadingSlots" class="grid grid-cols-7 gap-1">
      <div v-for="i in 35" :key="i" class="h-10 bg-gray-100 rounded-lg animate-pulse"></div>
    </div>

    <!-- Dias do mês -->
    <div v-else class="grid grid-cols-7 gap-1">
      <!-- Células vazias para alinhar o primeiro dia -->
      <div v-for="i in firstDayOfMonth" :key="'empty-' + i"></div>

      <!-- Dias -->
      <button
        v-for="day in daysInMonth"
        :key="day"
        @click="selectDay(day)"
        :disabled="!hasSlotsForDay(day) || isPastDay(day)"
        :class="[
          'h-10 rounded-lg text-sm font-medium transition relative',
          selectedDay === day
            ? 'bg-[#152a34] text-white'
            : hasSlotsForDay(day) && !isPastDay(day)
              ? 'bg-[#f4efe6] text-[#152a34] hover:bg-[#e9e0ce] cursor-pointer'
              : 'bg-gray-50 text-gray-300 cursor-not-allowed'
        ]"
      >
        {{ day }}
        <span
          v-if="hasSlotsForDay(day) && !isPastDay(day) && selectedDay !== day"
          class="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#b99a52]"
        ></span>
      </button>
    </div>

    <!-- Horários disponíveis -->
    <div v-if="selectedDay && availableSlotsForDay.length > 0" class="mt-5">
      <p class="text-sm font-medium text-[#152a34] mb-3">
        Horários disponíveis em {{ selectedDateLabel }}
      </p>
      <div class="grid grid-cols-3 md:grid-cols-4 gap-2">
        <button
          v-for="slot in availableSlotsForDay"
          :key="slot"
          @click="selectSlot(slot)"
          :class="[
            'py-2 rounded-xl text-sm font-medium border transition',
            selectedSlot === slot
              ? 'bg-[#b99a52] text-white border-[#b99a52]'
              : 'bg-white text-[#152a34] border-gray-200 hover:border-[#b99a52]'
          ]"
        >{{ slot }}</button>
      </div>
    </div>

    <div v-else-if="selectedDay && availableSlotsForDay.length === 0" class="mt-5 text-center text-sm text-gray-400">
      Nenhum horário disponível neste dia.
    </div>

    <!-- Nenhum slot disponível no mês inteiro -->
    <div v-if="!loadingSlots && slots.length === 0" class="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm flex items-start gap-2">
      <span class="mdi mdi-calendar-clock text-lg flex-shrink-0 mt-0.5"></span>
      <div>
        <p class="font-semibold">Nenhum horário disponível neste mês.</p>
        <p class="mt-0.5 text-amber-700">O prestador ainda não configurou sua agenda para este período. Tente outro mês ou entre em contato diretamente com o prestador.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { getProviderAvailability } from '@/services/api'

const props = defineProps({
  providerHash: { type: String, required: true },
  serviceId: { type: [Number, String], required: true }
})

const emit = defineEmits(['select'])

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const today = new Date()
const currentYear = ref(today.getFullYear())
const currentMonth = ref(today.getMonth() + 1)

const selectedDay = ref(null)
const selectedSlot = ref(null)
const slots = ref([])
const loadingSlots = ref(false)

const monthLabel = computed(() => {
  const d = new Date(currentYear.value, currentMonth.value - 1, 1)
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
})

const daysInMonth = computed(() =>
  new Date(currentYear.value, currentMonth.value, 0).getDate()
)

const firstDayOfMonth = computed(() =>
  new Date(currentYear.value, currentMonth.value - 1, 1).getDay()
)

const selectedDateLabel = computed(() => {
  if (!selectedDay.value) return ''
  const d = new Date(currentYear.value, currentMonth.value - 1, selectedDay.value)
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
})

const availableSlotsForDay = computed(() => {
  if (!selectedDay.value) return []
  const dateStr = `${currentYear.value}-${String(currentMonth.value).padStart(2, '0')}-${String(selectedDay.value).padStart(2, '0')}`
  const found = slots.value.find(s => s.date === dateStr)
  return found ? found.slots : []
})

function hasSlotsForDay (day) {
  const dateStr = `${currentYear.value}-${String(currentMonth.value).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  return slots.value.some(s => s.date === dateStr && s.slots.length > 0)
}

function isPastDay (day) {
  const d = new Date(currentYear.value, currentMonth.value - 1, day)
  const t = new Date()
  t.setHours(0, 0, 0, 0)
  return d < t
}

function selectDay (day) {
  if (!hasSlotsForDay(day) || isPastDay(day)) return
  selectedDay.value = day
  selectedSlot.value = null
}

function selectSlot (slot) {
  selectedSlot.value = slot
  const dateStr = `${currentYear.value}-${String(currentMonth.value).padStart(2, '0')}-${String(selectedDay.value).padStart(2, '0')}`
  emit('select', { date: dateStr, time: slot, datetime: `${dateStr}T${slot}:00` })
}

function prevMonth () {
  if (currentMonth.value === 1) {
    currentMonth.value = 12
    currentYear.value--
  } else {
    currentMonth.value--
  }
  selectedDay.value = null
  selectedSlot.value = null
  loadMonth()
}

function nextMonth () {
  if (currentMonth.value === 12) {
    currentMonth.value = 1
    currentYear.value++
  } else {
    currentMonth.value++
  }
  selectedDay.value = null
  selectedSlot.value = null
  loadMonth()
}

async function loadMonth () {
  loadingSlots.value = true
  try {
    const { data } = await getProviderAvailability(props.providerHash, {
      service_id: props.serviceId,
      year: currentYear.value,
      month: currentMonth.value
    })
    slots.value = data.data || []
  } catch (err) {
    slots.value = []
    console.error('Erro ao carregar disponibilidade:', err)
  } finally {
    loadingSlots.value = false
  }
}

onMounted(() => loadMonth())
watch(() => [props.providerHash, props.serviceId], loadMonth)
</script>
